<template>
  <InfuseTable
    :columns="columns"
    :actions="rowActions"
    :rows="rows"
    :loading="loading"
    :editable="editable"
    :removable="removable"
    :selectable="selectable"
    :row-key="(row: LinkedCentralSystemHydrated) => row.centralSystemId"
    :search="search"
    :has-more-items="false"
    :edit-permission-id="chargingStationPermissions.LINK"
    :remove-permission-id="chargingStationPermissions.LINK"
    :class-visitor="classVisitor"
    @click-row="(row: LinkedCentralSystem) => emit('click-row', row)"
    @load="emit('load')"
    @remove="(row: LinkedCentralSystem) => emit('remove', row)"
    @edit="(row: LinkedCentralSystem) => emit('edit', row)"
    @search="(query: string) => emit('search', query)"
    @selected="(rows: LinkedCentralSystemHydrated[]) => emit('selected', rows)"
    @action="onRowAction"
  >
    <template #actions>
      <InfuseButton
        v-permission="{ id: chargingStationPermissions.LINK, hide: false }"
        v-if="createEnabled"
        class="w-64"
        size="medium"
        @click="emit('create')"
      >
        <font-awesome-icon class="mr-1" :icon="['fas', 'link']" />
        {{ t('e_connector.chargingStationLinks.table.create_button') }}
      </InfuseButton>
    </template>

    <template #selectionActions="{ selectedRows }">
      <div class="flex gap-2">
        <InfuseButton
          v-permission="{ id: chargingStationPermissions.LINK, hide: false }"
          size="small"
          @click="emit('bulk-edit', selectedRows)"
        >
          <div class="flex items-center gap-1.5">
            <font-awesome-icon :icon="['far', 'pen-to-square']" />
            <span>{{ t('e_connector.chargingStationLinks.table.bulk_edit_button') }}</span>
          </div>
        </InfuseButton>
        <InfuseButton
          v-permission="{ id: chargingStationPermissions.LINK, hide: false }"
          color="alter"
          size="small"
          @click="emit('bulk-remove', selectedRows)"
        >
          <div class="flex items-center gap-1.5">
            <font-awesome-icon :icon="['far', 'trash-can']" />
            <span>{{ t('e_connector.chargingStationLinks.table.bulk_remove_button') }}</span>
          </div>
        </InfuseButton>
      </div>
    </template>

    <template #customChargingStationName="{ data }">
      <div class="italic">{{ data || '-' }}</div>
    </template>

    <template #type="{ data }">
      <InfuseTag v-if="data === LinkedCentralSystemType.MASTER" variant="primary">{{ data }}</InfuseTag>
      <InfuseTag v-else variant="neutral">{{ data }}</InfuseTag>
    </template>

    <template #commandsAllowed="{ data }">
      <font-awesome-icon
        :icon="['fas', data ? 'circle-check' : 'circle-xmark']"
        :class="data ? 'text-primary-500' : 'text-neutral-500'"
      ></font-awesome-icon>
    </template>

    <template #sessionAuthorizationAllowed="{ data }">
      <font-awesome-icon
        :icon="['fas', data ? 'circle-check' : 'circle-xmark']"
        :class="data ? 'text-primary-500' : 'text-neutral-500'"
      ></font-awesome-icon>
    </template>

    <template #forwardBasicAuth="{ data }">
      <font-awesome-icon
        :icon="['fas', data ? 'circle-check' : 'circle-xmark']"
        :class="data ? 'text-primary-500' : 'text-neutral-500'"
      ></font-awesome-icon>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { InfuseButton, InfuseTable, InfuseTag } from '@infuse/ui/components';
import { vPermission } from '@infuse/iam/permission/directives';
import { hasRequiredPermissions } from '@infuse/ui/utils';
import type { UiRowAction, UiTableColumn } from '@infuse/ui/domain';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { LinkedCentralSystem, LinkedCentralSystemType } from '@infuse/e-connector/central-system/domain';
import { LinkedCentralSystemHydrated } from '@infuse/e-connector/shared/feature';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { chargingStationPermissions } from '@infuse/api/e-connector/charging-station';

interface Emits {
  (e: 'click-row', row: LinkedCentralSystem): void;
  (e: 'create'): void;
  (e: 'load'): void;
  (e: 'remove', row: LinkedCentralSystem): void;
  (e: 'edit', row: LinkedCentralSystem): void;
  (e: 'search', query: string): void;
  (e: 'selected', rows: LinkedCentralSystemHydrated[]): void;
  (e: 'bulk-edit', rows: LinkedCentralSystemHydrated[]): void;
  (e: 'bulk-remove', rows: LinkedCentralSystemHydrated[]): void;
  (e: 'make-master', row: LinkedCentralSystemHydrated): void;
}

const emit = defineEmits<Emits>();

interface Props {
  createEnabled: boolean;
  editable: boolean;
  loading?: boolean;
  removable?: boolean;
  rows: LinkedCentralSystemHydrated[];
  searchEnabled: boolean;
  searchQuery?: string;
  selectable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  createEnabled: false,
  editable: false,
  loading: false,
  removable: false,
  searchEnabled: true,
  searchQuery: '',
  selectable: false
});

const { t } = useI18n();
const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('e_connector.common.table_search_all'),
        query: props.searchQuery
      }
);

// the selection bar defaults to floating over the bottom rows; in the slide-in the table
// doesn't fill the panel, so let it flow below the table instead
const classVisitor = (key: string) =>
  key === 'dataTableCounterClass' ? 'w-[36rem] rounded border border-gray-100 bg-white px-7 shadow' : '';

const rowActions: UiRowAction[] = [
  {
    icon: ['fas', 'crown'],
    label: () => t('e_connector.chargingStationLinks.table.make_master_action'),
    enabled: (row: LinkedCentralSystemHydrated) =>
      row.type === LinkedCentralSystemType.READONLY &&
      hasRequiredPermissions([chargingStationPermissions.LINK], 'all', false),
    event: 'make-master',
    onlyShowOnHover: true
  }
];

function onRowAction(event: string, row: LinkedCentralSystemHydrated) {
  if (event === 'make-master') {
    emit('make-master', row);
  }
}

const columns: UiTableColumn[] = [
  {
    name: 'centralSystemName',
    label: () => t('e_connector.common.chargingStationLink.centralSystemName'),
    field: 'centralSystem.name',
    bold: true
  },
  {
    name: 'customChargingStationName',
    label: () => t('e_connector.common.chargingStationLink.customChargingStationName'),
    field: 'customChargingStationName'
  },
  {
    name: 'type',
    label: () => t('e_connector.common.chargingStationLink.type'),
    field: 'type'
  },
  {
    name: 'commandsAllowed',
    label: () => t('e_connector.common.chargingStationLink.commandsAllowed'),
    field: 'commandsAllowed'
  },
  {
    name: 'sessionAuthorizationAllowed',
    label: () => t('e_connector.common.chargingStationLink.authorizationAllowed'),
    field: 'sessionAuthorizationAllowed'
  },
  {
    name: 'forwardBasicAuth',
    label: () => t('e_connector.common.chargingStationLink.forwardBasicAuth'),
    field: 'forwardBasicAuth'
  }
];
</script>
